/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

const KM_PER_DEGREE = 111.32
// Longitude degrees shrink to zero at the poles, so cap the latitude used to size them.
const MAX_SIZING_LATITUDE = 89

const clamp = (value, limit) => Math.min(Math.max(value, -limit), limit)
const format = (value) => String(Number(value.toFixed(6)))
const sizingLatRadians = (lat) => Math.min(Math.abs(lat), MAX_SIZING_LATITUDE) * (Math.PI / 180)
// Rounding each bound to 6 decimals shifts a box by at most this much.
const CENTER_TOLERANCE_DEGREES = 1e-5

// Returns north/south/east/west (as strings) for a box reaching `radiusKm` from the center
// in each direction, kept inside valid latitude and longitude ranges.
export const boundsAround = (lat, lng, radiusKm) => {
  const latDelta = radiusKm / KM_PER_DEGREE
  const lngDelta = radiusKm / (KM_PER_DEGREE * Math.cos(sizingLatRadians(lat)))

  return {
    north: format(clamp(lat + latDelta, 90)),
    south: format(clamp(lat - latDelta, 90)),
    east: format(clamp(lng + lngDelta, 180)),
    west: format(clamp(lng - lngDelta, 180)),
  }
}

// Radius in km (as a string, rounded to 2 decimals) of a box centered on the center and square,
// as `boundsAround` builds. '' otherwise, including boxes clamped at a pole or the 180° line.
// A hand-typed box that happens to be centered and square also gets a radius.
export const radiusOf = ({ center_lat, center_lng, north, south, east, west }) => {
  const raw = [center_lat, center_lng, north, south, east, west]
  if (raw.some((value) => value === null || value === undefined || value === '')) return ''

  const numbers = raw.map(Number)
  const [lat, lng, n, s, e, w] = numbers
  if (!numbers.every(Number.isFinite) || n <= s || e <= w) return ''

  const latRadius = ((n - s) / 2) * KM_PER_DEGREE
  const lngRadius = ((e - w) / 2) * KM_PER_DEGREE * Math.cos(sizingLatRadians(lat))
  const centered = Math.abs(lat - (n + s) / 2) <= CENTER_TOLERANCE_DEGREES
    && Math.abs(lng - (e + w) / 2) <= CENTER_TOLERANCE_DEGREES
  const square = Math.abs(latRadius - lngRadius) <= Math.max(0.005, latRadius * 0.001)

  return centered && square ? String(Number(latRadius.toFixed(2))) : ''
}
